/**
 * Chart color palette, sourced from the Atlassian Design System token package
 * (@atlaskit/tokens — the "bolder" step of each accent hue, the strength meant
 * for foreground marks/text on a light surface).
 *
 * Validated against the six categorical checks (lightness band, chroma floor,
 * CVD separation, normal-vision floor, contrast vs surface) — see the six-hue
 * theme below, which passes all six with light-surface #fcfcfb:
 *
 *   node validate_palette.js "#1868DB,#C9372C,#1F845A,#964AC0,#BD5B00,#AE4787" --mode light
 *   → ALL CHECKS PASS (green/red adjacent sits in the 6-8 CVD floor band —
 *     legal only with a legend/direct labels, which every chart here ships)
 *
 * This app has no dark mode (see src/styles/globals.css), so only a light
 * instance is defined.
 */

/** Categorical — identity, fixed order, assigned in sequence, never cycled or re-colored by rank. */
export const CATEGORICAL_CHART_COLORS = [
  '#1868DB', // blue
  '#C9372C', // red
  '#1F845A', // green
  '#964AC0', // purple
  '#BD5B00', // orange
  '#AE4787', // magenta
] as const;

/**
 * Status — state, reserved meaning, never reused for "series N". Always paired
 * with an icon + label (never color alone) — warning/critical sit close by
 * design (Atlassian's own tokens), and the label is the mitigation.
 */
export const STATUS_CHART_COLORS = {
  good: '#1F845A',
  warning: '#BD5B00',
  critical: '#C9372C',
  neutral: '#6B6E76',
} as const;

/** ~10% area-fill wash of a mark color, for chart fills/backgrounds (never a saturated block). */
export function chartWash(hex: string, alpha = 0.1): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
